import React, { useLayoutEffect, useRef, useState } from 'react';
import NPCDialogueText from './NPCDialogueText';

interface Props {
  value: string;
  messageNumber: number;
  onChange: (value: string) => void;
  disabled?: boolean;
}

const NPCDialogueEditor: React.FC<Props> = ({ value, messageNumber, onChange, disabled = false }) => {
  const textarea = useRef<HTMLTextAreaElement>(null);
  const selection = useRef<[number, number] | null>(null);
  const [limitReached, setLimitReached] = useState(false);

  useLayoutEffect(() => {
    if (!selection.current || !textarea.current) return;
    textarea.current.focus();
    textarea.current.setSelectionRange(...selection.current);
    selection.current = null;
  }, [value]);

  const applyChange = (next: string, range: [number, number]) => {
    if (next.length > 1000) {
      setLimitReached(true);
      return;
    }
    selection.current = range;
    setLimitReached(false);
    onChange(next);
  };

  const format = (before: string, after: string, placeholder: string, link = false) => {
    const start = textarea.current?.selectionStart ?? value.length;
    const end = textarea.current?.selectionEnd ?? value.length;
    const text = value.slice(start, end) || placeholder;
    const next = value.slice(0, start) + before + text + after + value.slice(end);
    // Select the URL after inserting a link so staff can immediately replace it.
    const selectionStart = link ? start + before.length + text.length + 2 : start + before.length;
    applyChange(next, [selectionStart, selectionStart + (link ? 'https://example.com'.length : text.length)]);
  };

  const formatList = () => {
    const start = textarea.current?.selectionStart ?? value.length;
    const end = textarea.current?.selectionEnd ?? value.length;
    const lineStart = value.lastIndexOf('\n', start - 1) + 1;
    const nextBreak = value.indexOf('\n', end);
    const lineEnd = nextBreak === -1 ? value.length : nextBreak;
    const text = value.slice(lineStart, lineEnd) || 'List item';
    const list = text.split('\n').map((line) => line.startsWith('- ') ? line : `- ${line}`).join('\n');
    applyChange(value.slice(0, lineStart) + list + value.slice(lineEnd), [lineStart + 2, lineStart + list.length]);
  };

  const tools = [
    { label: 'Bold', run: () => format('**', '**', 'bold text') },
    { label: 'Italic', run: () => format('*', '*', 'italic text') },
    { label: 'Link', run: () => format('[', '](https://example.com)', 'link text', true) },
    { label: 'Bullet list', run: formatList },
  ];

  return (
    <div className="min-w-0 flex-1 space-y-2">
      <div role="group" aria-label={`Formatting for message ${messageNumber}`} className="flex flex-wrap gap-1">
        {tools.map(({ label, run }) => (
          <button key={label} type="button" disabled={disabled} onMouseDown={(event) => event.preventDefault()} onClick={run}
            className="rounded border border-gray-600 bg-gray-800 px-2 py-1 text-xs text-cyan-200 hover:bg-gray-700 disabled:opacity-50">
            {label}
          </button>
        ))}
      </div>
      <textarea
        ref={textarea}
        value={value}
        disabled={disabled}
        maxLength={1000}
        rows={3}
        aria-label={`Dialogue message ${messageNumber}`}
        aria-describedby={`npc-message-help-${messageNumber}`}
        onChange={(event) => { setLimitReached(false); onChange(event.target.value); }}
        className="min-h-[3.5rem] w-full resize-y rounded-md border border-gray-600 bg-gray-800 px-3 py-2 text-sm text-white outline-none focus:border-cyan-500"
      />
      <p id={`npc-message-help-${messageNumber}`} className="text-xs text-gray-400">
        Markdown supported: **bold**, *italic*, [link text](https://example.com). {value.length}/1000
      </p>
      {limitReached && <p role="alert" className="text-xs text-red-300">Formatting would exceed the 1,000-character message limit.</p>}
      <div role="region" aria-label={`Preview message ${messageNumber}`} className="rounded border border-gray-700 bg-gray-800 p-3 text-sm">
        <p className="mb-2 text-xs font-semibold text-gray-400">Preview</p>
        {value.trim() ? <NPCDialogueText>{value}</NPCDialogueText> : <p className="text-gray-500">Your message will appear here.</p>}
      </div>
    </div>
  );
};

export default NPCDialogueEditor;
